import { PrismaEnums } from '@airreading/common';
import { Schedule } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getSchedules, updateSchedulesStatus } from '@/lib/api-calls';

const formSchema = z.object({
  status: z.nativeEnum(PrismaEnums.ScheduleStatus),
});

type FormSchema = z.infer<typeof formSchema>;
interface UpdateScheduleStatusProps {
  scheduleOrIds: string[] | Schedule;
  trigger: ReactNode;
  onSuccess?: () => void;
}

export const UpdateScheduleStatus = ({ scheduleOrIds, trigger, onSuccess }: UpdateScheduleStatusProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: updateSchedulesStatus,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const onSubmit = ({ formData, reset, closeModal }: SubmitProps<FormSchema>) => {
    const { status } = formData;

    return mutateAsync({
      status,
      scheduleIds: Array.isArray(scheduleOrIds) ? scheduleOrIds : [scheduleOrIds.id],
    })
      .then(() => {
        toast({
          description: 'Successfully updated schedule status',
        });
        reset();
        closeModal();
        queryClient.invalidateQueries(getSchedules());
        onSuccess?.();
      })
      .catch(() => {
        toast({
          description: 'Failed to update schedule status',
        });
      });
  };

  return (
    <FormModal
      modalTitle="Update Schedule Status"
      onSubmit={onSubmit}
      formSchema={formSchema}
      trigger={trigger}
      defaultValues={
        Array.isArray(scheduleOrIds)
          ? undefined
          : {
              status: scheduleOrIds.status,
            }
      }
    >
      {({ form }) => (
        <div className="grid grid-cols-1 gap-4 py-4">
          <FormSingleSelect
            form={form}
            name="status"
            options={Object.values(PrismaEnums.ScheduleStatus)}
            label="Status"
          />
        </div>
      )}
    </FormModal>
  );
};
